import type { ChatMessage, RootState } from "../types/redux";

/**
 * Select visible messages.
 * Phase 3: All messages are visible now - no internal placeholders.
 */
export const selectVisibleMessages = (state: RootState): ChatMessage[] => {
  return state.chatSlice?.messages || [];
};

/** Get todo plan for a specific turn. */
export const selectTurnTodoPlan = (state: RootState, turnId: string): unknown => {
  const turnData = state.chatSlice?.todoPlanByTurn?.[turnId];
  return turnData && typeof turnData === "object" && "plan" in turnData
    ? (turnData as { plan: unknown }).plan
    : null;
};

/** Get all todo plans. */
export const selectAllTodoPlans = (state: RootState): Record<string, unknown> => {
  return state.chatSlice?.todoPlanByTurn || {};
};

/** Get the current active turn ID (from pending streaming messages). */
export const selectCurrentTurnId = (state: RootState): string | null => {
  const pendingMessages = state.chatSlice?.pendingStreamingMessages || {};
  const turnIds = Object.keys(pendingMessages);
  if (turnIds.length > 0) {
    return turnIds[0] || null;
  }

  const todoPlans = state.chatSlice?.todoPlanByTurn || {};
  const planTurnIds = Object.keys(todoPlans);
  if (planTurnIds.length > 0) {
    return planTurnIds[planTurnIds.length - 1] || null;
  }

  return null;
};

/** Get the current turn's todo plan (convenience selector). */
export const selectCurrentTurnTodoPlan = (state: RootState): unknown => {
  const currentTurnId = selectCurrentTurnId(state);
  return currentTurnId ? selectTurnTodoPlan(state, currentTurnId) : null;
};

/** Get chat history loading state. */
export const selectChatHistoryLoading = (state: RootState): boolean => {
  return state.chatSlice?.chatHistoryLoading || false;
};

/** Get chat history error. */
export const selectChatHistoryError = (state: RootState): string | null => {
  return state.chatSlice?.chatHistoryError || null;
};

/** Get currently selected use case (e.g. 'past-deals', 'research-desk'). */
export const selectSelectedUseCase = (state: RootState): string | null => {
  return state.chatSlice?.selectedUseCase || null;
};
